import { RowChartValueKeys } from 'src/app/dashboards/shared/charts/chart-row/chart-row.component';
import {
  Granularity,
  Metric,
} from '../../../../dashboard-config/dashboard-config.model';
import { KpiMeasure } from '../../../../dashboard-config/kpis/kpi-columns';
import { MetricProfile } from '../../../../dashboard-config/metrics/metric-profile';
import {
  companiesCountProfile,
  companiesRevenueProfile,
  economicParticipantsProfile,
  employedProfile,
  gvaProfile,
  selfEmployedProfile,
} from 'src/app/dashboards/shared/dashboard-config/metrics/metric-profile-implementations';
import { RankingPopupSlot } from './dashboard-ranking.definition';
import { GRANULARITY_PROFILES } from '../../../../dashboard-config/granularities/granularity-profile';

export const RANKING_VALUE_KEY = RowChartValueKeys.value;
export const RANKING_VALUE_SHARE_PCT_KEY = RowChartValueKeys.valueSharePct;

export interface RankingTimelineConfig {
  /** Filter used by chart timeline playback (year or period_value). */
  filterName: string;
  /** Option field used to set that filter’s value. */
  optionValueKey: string;
}

/** A KPI measure copied into a chart row key. */
export interface RankingMeasure {
  measure: KpiMeasure;
  resultColumn: string;
}

/**
 * Ranking exceptions of a metric. Everything else comes from `base` (KPIs, temporal kind,
 * labels), the granularity profile and `ProductRules`; unset fields take the defaults below.
 */
export interface RankingMetricProfile {
  metricId: Metric;
  base: MetricProfile;
  /** Prefer employed-districts bar width (80) when shell is districts. */
  narrowDistrictValueCol?: boolean;
  /** Count and revenue datasets carry a share of the state at district level. */
  districtShareOfState?: boolean;
  /** Overrides value + share of parent (Employed: district value and population share). */
  chartMeasures?: RankingMeasure[];
  /** Ranks over chart keys beyond `rank` (e.g. employed district population share). */
  extraRanks?: { rankColumn: string; valueColumn: string }[];
  /** Overrides `RANKING ${KPI genitive phrase}`. */
  chartHeadingLead?: (filterValues: Map<string, any>) => string;
  /** Overrides the selected KPI's nominative label. */
  mapHeadingLead?: (filterValues: Map<string, any>) => string;
  popupSlot?: RankingPopupSlot;
}

/** Share of the parent next to a value: of the country for states, of the state at district level (where the dataset has it), else of the sum of sub-markets. */
export function rankingShareMeasure(metric: RankingMetricProfile, granularity: Granularity): KpiMeasure {
  if (granularity === Granularity.States) return 'shareOfCountryPct';
  if (metric.districtShareOfState && GRANULARITY_PROFILES[granularity].traits.includes('district-level')) return 'shareOfStatePct';
  return 'shareOfSumSubMarketPct';
}

export function rankingChartMeasures(metric: RankingMetricProfile, granularity: Granularity): RankingMeasure[] {
  return (
    metric.chartMeasures ?? [
      { measure: 'value', resultColumn: RANKING_VALUE_KEY },
      { measure: rankingShareMeasure(metric, granularity), resultColumn: RANKING_VALUE_SHARE_PCT_KEY },
    ]
  );
}

export function rankingChartHeadingLead(metric: RankingMetricProfile): (filterValues: Map<string, any>) => string {
  return metric.chartHeadingLead ?? ((fv) => `RANKING ${metric.base.formatKpiGenitivePhrase(fv)}`);
}

export function rankingMapHeadingLead(metric: RankingMetricProfile): (filterValues: Map<string, any>) => string {
  return metric.mapHeadingLead ?? ((fv) => metric.base.formatKpiLabel(fv));
}

export const rankingCompaniesCountProfile: RankingMetricProfile = {
  metricId: Metric.CompaniesCount,
  base: companiesCountProfile,
  districtShareOfState: true,
};

export const rankingCompaniesRevenueProfile: RankingMetricProfile = {
  metricId: Metric.CompaniesRevenue,
  base: companiesRevenueProfile,
  districtShareOfState: true,
  // Shell-local compound verb; shared lexicon is UMSÄTZE VON + type label.
  chartHeadingLead: (fv) => `UMSATZRANKING VON ${companiesRevenueProfile.formatKpiLabel(fv)}`,
};

export const rankingGrossValueAddedProfile: RankingMetricProfile = {
  metricId: Metric.GrossValueAdded,
  base: gvaProfile,
  districtShareOfState: true,
};

export const rankingEconomicParticipantProfile: RankingMetricProfile = {
  metricId: Metric.EconomicParticipant,
  base: economicParticipantsProfile,
  popupSlot: 'economic_participants',
};

export const rankingEmployedProfile: RankingMetricProfile = {
  metricId: Metric.Employed,
  base: employedProfile,
  narrowDistrictValueCol: true,
  // Employed shows district value and population share instead of a parent share.
  chartMeasures: [
    { measure: 'value', resultColumn: RANKING_VALUE_KEY },
    { measure: 'districtValue', resultColumn: RANKING_VALUE_KEY + '_district' },
    { measure: 'districtPopulationPct', resultColumn: RANKING_VALUE_KEY + '_district_population_pct' },
  ],
  extraRanks: [
    { rankColumn: 'rank_district_population_pct', valueColumn: RANKING_VALUE_KEY + '_district_population_pct' },
  ],
  popupSlot: 'employed',
};

export const rankingSelfEmployedProfile: RankingMetricProfile = {
  metricId: Metric.SelfEmployed,
  base: selfEmployedProfile,
  mapHeadingLead: () => 'SOLO-SELBSTSTÄNDIGEN',
};
